import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingBus,
  createTrackingBus,
  updateTrackingBus,
  deleteTrackingBus,
  type TrackingBus,
  type BusUpsertPayload,
} from "../services/tracking/busService";

const KEY = "tracking-bus";

export function useTrackingBus() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingBus[]>(
    token ? [KEY, token] : null,
    () => fetchTrackingBus(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);
  const list = Array.isArray(data) ? data : [];

  const create = useCallback(
    async (payload: BusUpsertPayload) => {
      const created = await createTrackingBus(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const update = useCallback(
    async (id: string, payload: BusUpsertPayload) => {
      const updated = await updateTrackingBus(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteTrackingBus(token, id);
      await mutate();
    },
    [token, mutate]
  );

  return {
    buses: list,
    isLoading,
    error,
    refetch,
    create,
    update,
    remove,
    accessToken: token,
  };
}

export type { TrackingBus, BusUpsertPayload };
